

import { getColumn, toISTInputValue } from "../../utils/fn";

// ADD these NEW functions at the TOP of fn.js (after imports):

/**
 * Converts YYYY-MM-DD to DD-MM-YYYY for display
 */
export const formatDateForDisplay = (isoDate) => {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

/**
 * Converts DD-MM-YYYY to YYYY-MM-DD for input value
 */
export const parseDateFromDisplay = (displayDate) => {
  if (!displayDate) return "";
  const [day, month, year] = displayDate.split("-");
  return `${year}-${month}-${day}`;
};

/**
 * Converts date to start of day (00:00:00.000Z) for backend
 */
export const toStartOfDay = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  date.setHours(0, 0, 0, 0);
  return date.toISOString();
};

/**
 * Converts date to end of day (23:59:59.999Z) for backend
 */
export const toEndOfDay = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  date.setHours(23, 59, 59, 999);
  return date.toISOString();
};

const token = localStorage.getItem("zTaskAuthToken");

export const getDetailsFromFormFields = (formFields) => {
  const fields = [];
  const defaultValues = {};

  for (const element of formFields) {
    if (fields[element.sequence] === undefined) {
      fields[element.sequence] = [element];
    } else {
      fields[element.sequence].push(element);
    }

    // Handle default values
    if (element.tag === "input" && element.inputType === "date") {
      const currentDate = new Date();
      
      if (element.defaultValue === "now") {
        // For "now", use current date in YYYY-MM-DD format
        defaultValues[element.fieldName] = currentDate.toISOString().slice(0, 10);
      } else if (
        typeof element.defaultValue === "object" &&
        element.defaultValue?.time
      ) {
        // For end of day, still use YYYY-MM-DD but store the time intent
        currentDate.setHours(...element.defaultValue.time, 0, 0);
        defaultValues[element.fieldName] = currentDate.toISOString().slice(0, 10);
      } else if (typeof element.defaultValue === "function") {
        // Let the function handle it
        defaultValues[element.fieldName] = element.defaultValue;
      } else {
        defaultValues[element.fieldName] = element.defaultValue || null;
      }
    } else {
      defaultValues[element.fieldName] =
        typeof element.defaultValue === "undefined"
          ? null
          : element.defaultValue;
    }
  }

  return { fields, defaultValues, dateFields: null };
};

export const checkIfValuesExist = (data, keys) => {
  for (const element of keys) {
    if (!data[element]) return false;
  }
  return true;
};

// MODIFY the existing getValueWithType function:
export const getValueWithType = (field, value) => {
  switch (field.inputType) {
    case "date":
      if (!value) return "";
      // Extract just the date part (YYYY-MM-DD) from ISO string
      const dateStr = String(value).split("T")[0];
      return dateStr; // Return YYYY-MM-DD for input value
    
    case "datetime-local":
      // This case should no longer be used, but keeping for safety
      console.warn("datetime-local deprecated, use date instead");
      return value ? new Date(value).toISOString().slice(0, 16) : "";
    
    case "number":
      return isNaN(Number(value)) ? 0 : Number(value);
    
    default:
      if (typeof value === "undefined" || value === null) {
        return "";
      }
      return typeof value !== "string" ? String(value) : value;
  }
};


export const fetchDropdowns = async (fields, data, setDropdownOptions) => {
  console.log(data);

  let dropdownOptions = {};
  for (const field of fields) {
    if (field.tag === "select") {
      if (!field.data?.dependsOnField || data[field.data?.dependsOnField]) {
        let filter = {};
        const options = await getDropdownOptions(field, data, filter);
        dropdownOptions = {
          ...dropdownOptions,
          [field.fieldName]: options,
        };
      }
    }
    if (field.tag == "combobox" && !field.data?.dependsOnField) {
      const options = await field.getOptions();
      dropdownOptions = {
        ...dropdownOptions,
        [field.fieldName]: options,
      };
    }
  }
  setDropdownOptions((prev) => ({
    ...prev,
    ...dropdownOptions,
  }));
};

export const getDropdownOptions = async (field, data, filter) => {
  if (typeof field.data?.filter === "function") {
    filter = { ...filter, ...field.data.filter(data) };
  } else {
    filter = { ...filter, ...field.data?.filter };
  }
  if (Array.isArray(field.data?.dependsOnField)) {
    field.data.dependsOnField.forEach((item) => {
      filter[item] = data[item];
    });
  } else {
    if (field.data?.dependsOnField)
      filter[field.data.dependsOnField] = data[field.data.dependsOnField];
  }

  if (
    field.isMulti &&
    data[field.fieldName] &&
    typeof data[field.fieldName] === "string"
  ) {
    try {
      data[field.fieldName] = JSON.parse(data[field.fieldName]);
    } catch (e) {
      console.error(
        "Failed to parse multi-select string:",
        data[field.fieldName],
        e
      );
      data[field.fieldName] = [];
    }
  }

  let options = [];
  if (field.getOptions && field.getOptionLabel && field.getOptionValue) {
    options = await field.getOptions(data);
    console.log(options);
    return options;
  }

  if (field.data?.tableName) {
    options = await getColumn(
      field.data.tableName,
      field.data.columnName,
      filter
    );
  } else if (field.data?.url) {
    let url = "";
    if (typeof field.data?.url == "function") {
      url = field.data?.url(data);
    }
    url =
      field.data.type == "query"
        ? `${field.data.url}?${new URLSearchParams(filter)}`
        : field.data.url;
    options = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      ...(field.data.type == "query" ? [{ body: filter }] : []),
    });
    if (field.data?.processOptions) {
      if (typeof field.data?.processOptions == "function") {
        options = await field.data?.processOptions(options, data);
      }
    }
  }

  return options;
};

export function objectToQueryString(obj) {
  const parts = [];

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      let encodedValue;

      // Handle arrays and nested objects by stringifying and encoding them
      if (typeof value === "object" && value !== null) {
        encodedValue = encodeURIComponent(JSON.stringify(value)); // Stringify and encode complex types
      } else {
        encodedValue = encodeURIComponent(value); // Encode simple values
      }

      parts.push(`${encodeURIComponent(key)}=${encodedValue}`); // Encode key and value
    }
  }

  return parts.join("&"); // Join key-value pairs with '&'
}

export function areObjectsEqual(objA, objB) {
  if (objA === objB) return true; // Check for referential equality first

  if (
    typeof objA !== "object" ||
    objA === null ||
    typeof objB !== "object" ||
    objB === null
  ) {
    return false; // Not both objects or one is null
  }

  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);

  // Use case sepcific
  if (keysA.length > keysB.length) {
    return true;
  } else if (keysA.length !== keysB.length) {
    return false; // Different number of properties
  }

  for (const key of keysA) {
    if (
      !(keysB.includes(key) && objA[key] == objB[key]) ||
      !areObjectsEqual(objA[key], objB[key])
    ) {
      return false; // Property missing or values differ
    }
  }
  return true;
}

export const updateStatus = async (
  listApi,
  id,
  stage,
  to,
  remark,
  successMessage,
  approvers
) => {
  const url = `${listApi}/status/${id}`;
  const res = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      stage,
      to,
      remarks: remark,
      approvers,
    }),
  });
  if (res.ok) {
    showMessageBox(successMessage);
    console.log(res);
    const data = await res.json();
    console.log(data);
  } else {
    const errorData = await res.json();
    return errorData;
  }
};

const MESSAGE_BOX_TITLES = {
  error: "Error",
  success: "Success",
  info: "Notice",
  warning: "Warning",
  confirm: "Confirm",
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/**
 * Centered modal popup for success / error / info messages.
 * @param {string} message
 * @param {"error"|"success"|"info"|"warning"} [type="info"]
 */
export const showMessageBox = (message, type = "info") => {
  const normalizedType = MESSAGE_BOX_TITLES[type] ? type : "info";
  document.querySelectorAll(".custom-alert").forEach((el) => el.remove());

  const alertBox = document.createElement("div");
  alertBox.className = `custom-alert custom-alert-${normalizedType}`;
  alertBox.setAttribute("role", "alertdialog");
  alertBox.setAttribute("aria-modal", "true");
  alertBox.innerHTML = `
      <div class="custom-alert-content">
        <h3 class="custom-alert-title">${MESSAGE_BOX_TITLES[normalizedType]}</h3>
        <p>${escapeHtml(message)}</p>
        <button type="button" class="custom-alert-ok">OK</button>
      </div>
    `;

  const close = () => {
    alertBox.remove();
    document.removeEventListener("keydown", onKeyDown);
  };
  const onKeyDown = (e) => {
    if (e.key === "Escape" || e.key === "Enter") close();
  };

  alertBox.addEventListener("click", (e) => {
    if (e.target === alertBox) close();
  });
  alertBox.querySelector(".custom-alert-ok")?.addEventListener("click", close);
  document.addEventListener("keydown", onKeyDown);
  document.body.appendChild(alertBox);
  alertBox.querySelector(".custom-alert-ok")?.focus();
};

